import {
  faCheckCircle,
  faCopy,
  faExclamationCircle,
  faPlus,
  faRobot,
  faRotateRight,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { AgentVersionName } from "src/agents/AgentVersionName";
import { CreateAgentVersionModal } from "src/agents/CreateAgentVersionModal";
import {
  useAgentVersions,
  useDeleteAgentVersion,
  usePublishAgentVersion,
} from "src/agents/queries";
import { AgentVersion } from "src/agents/types";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { DisplayDateTime } from "src/base-components/Date";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import { ExcludesFalse } from "src/flow/types";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { tableWidth } from "src/layout/constants";
import { getUrlToAgentVersionPage } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentVersionsPage: React.FC = () => {
  const { agentId } = useParamsDecode<AgentVersionsPageParams>();
  const [filter, setFilter] = useState<FilterValue | null>(null);
  const {
    isOpen: isCreateModalOpen,
    data: createModalData,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
  } = useModal<{ versionId?: string; startFromScratch: boolean }>();

  useDocumentTitle(["Agent Versions"]);

  const visibleTables = filter === null ? "both" : filter;

  return (
    <div className="flex h-full flex-col" data-loc="agent-versions-page">
      <SubHeader title="Versions">
        <SubHeaderFilter
          dataLoc="filter-versions"
          options={filterOptions}
          selected={filter}
          onChange={setFilter}
        />
        <SubHeader.Button
          dataLoc="create-version-button"
          icon={faPlus}
          tooltip="Create version"
          onClick={() => openCreateModal({ startFromScratch: true })}
        />
      </SubHeader>
      <div className="decideScrollbar flex h-full min-w-[300px] flex-col overflow-x-auto">
        <AgentVersionsContent
          agentId={agentId}
          visibleTables={visibleTables}
          onOpenCreateModal={openCreateModal}
        />
      </div>
      <CreateAgentVersionModal
        agentId={agentId}
        open={isCreateModalOpen}
        startFromScratch={createModalData?.startFromScratch ?? false}
        versionId={createModalData?.versionId}
        onClose={closeCreateModal}
      />
    </div>
  );
};

type AgentVersionsPageParams = {
  orgId: string;
  wsId: string;
  agentId: string;
};

const filterOptions = [
  { key: "published" as const, value: "Published" },
  { key: "draft" as const, value: "Draft" },
];

type FilterValue = (typeof filterOptions)[number]["key"];

type AgentVersionsContentProps = {
  agentId: string;
  visibleTables: "published" | "draft" | "both";
  onOpenCreateModal: (data?: {
    versionId?: string;
    startFromScratch: boolean;
  }) => void;
};

const AgentVersionsContent: React.FC<AgentVersionsContentProps> = ({
  agentId,
  visibleTables,
  onOpenCreateModal,
}) => {
  const {
    data: versions,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useAgentVersions(agentId);

  const publishMutation = usePublishAgentVersion();
  const deleteMutation = useDeleteAgentVersion();

  const {
    isOpen: isDeleteModalOpen,
    data: versionToDelete,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal<AgentVersion>();

  const handleDuplicate = (version: AgentVersion) => {
    onOpenCreateModal({ versionId: version.id, startFromScratch: false });
  };

  const handlePublish = (version: AgentVersion) => {
    if (version.status === "published") {
      logger.error("Cannot publish published version", version);
      return;
    }

    publishMutation.mutate({
      agentId,
      versionId: version.id,
      etag: version.etag,
    });
  };

  const handleDelete = (version: AgentVersion) => {
    openDeleteModal(version);
  };

  const handleConfirmDelete = () => {
    if (!versionToDelete) return;

    deleteMutation.mutate({
      agentId,
      versionId: versionToDelete.id,
    });
    closeDeleteModal();
  };

  const publishedVersions =
    versions?.filter((version) => version.status === "published") ?? [];

  const draftVersions =
    versions?.filter((version) => version.status === "draft") ?? [];

  if (isError) {
    return (
      <div className="p-5">
        <WhitePane>
          <EmptyState
            actionButton={{
              icon: faRotateRight,
              text: "Retry",
              onClick: refetch,
              disabled: isFetching,
            }}
            dataLoc="agent-versions-error-state"
            description="We're having trouble loading agent versions. Please try again later"
            headline="Error loading versions"
            icon={faExclamationCircle}
            variant="error"
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <>
      <div
        className={twJoin(
          "mx-auto flex w-full flex-col gap-y-5.5 p-5",
          tableWidth,
        )}
      >
        {!isLoading &&
          publishedVersions.length === 0 &&
          draftVersions.length === 0 && (
            <WhitePane>
              <EmptyState
                actionButton={{
                  text: "Create version",
                  icon: faPlus,
                  onClick: () => onOpenCreateModal({ startFromScratch: true }),
                }}
                dataLoc="agent-versions-empty-state"
                description="Create your first version to get started"
                headline="No versions yet"
                icon={faRobot}
              />
            </WhitePane>
          )}

        {visibleTables !== "draft" &&
          (publishedVersions.length > 0 || isLoading) && (
            <AgentVersionsTable
              canDelete={false}
              isLoading={isLoading}
              versions={publishedVersions}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onPublish={handlePublish}
            />
          )}

        {visibleTables !== "published" && draftVersions.length > 0 && (
          <AgentVersionsTable
            canDelete={true}
            isLoading={isLoading}
            versions={draftVersions}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onPublish={handlePublish}
          />
        )}
      </div>

      <ConfirmationModal
        confirmationButtonText="Delete"
        description={`Are you sure you want to delete version "${versionToDelete?.name}"? This action cannot be undone.`}
        open={isDeleteModalOpen}
        title="Delete version"
        variant="danger"
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

const AgentVersionsTable: React.FC<{
  versions: AgentVersion[];
  isLoading: boolean;
  canDelete: boolean;
  onDuplicate: (version: AgentVersion) => void;
  onPublish: (version: AgentVersion) => void;
  onDelete: (version: AgentVersion) => void;
}> = ({ versions, isLoading, canDelete, onDuplicate, onPublish, onDelete }) => {
  const navigate = useNavigate();
  const { agentId, orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();

  const actions: Parameters<typeof Table>[0]["actions"] = {
    onDuplicate,
    onPublish,
  };
  if (canDelete) {
    actions.onDelete = onDelete;
  }

  return (
    <WhitePane>
      <Table
        actions={actions}
        columns={COLUMNS}
        data={versions}
        dataLoc="agent-versions-table"
        frameClassName="w-full"
        isLoading={isLoading}
        loadingRowsCount={3}
        rowClassName="cursor-pointer hover:bg-gray-50"
        rowPropsGetter={(row) => ({
          "data-loc": `agent-version-row-${row.original.name}`,
          onClick: () => {
            navigate(
              getUrlToAgentVersionPage(orgId, wsId, agentId, row.original.id),
            );
          },
        })}
      />
    </WhitePane>
  );
};

const helper = createColumnHelper<AgentVersion>();

const COLUMNS = [
  helper.accessor("name", {
    header: (info) => {
      const isDraftTable =
        info.table.getRowModel().rows.at(0)?.original.status === "draft";
      return (
        <Header info={info}>
          <div className="pl-5">{isDraftTable ? "Draft" : "Published"}</div>
        </Header>
      );
    },
    cell: (info) => (
      <Cell className="pl-5" info={info}>
        <AgentVersionName
          name={info.getValue()}
          status={info.row.original.status}
        />
      </Cell>
    ),
  }),
  helper.accessor((row) => row.metadata.created_at, {
    id: "created_at",
    header: (info) => <Header info={info}>Created at</Header>,
    cell: (info) => (
      <Cell info={info}>
        {info.getValue() ? <DisplayDateTime value={info.getValue()} /> : "-"}
      </Cell>
    ),
    size: 300,
  }),
  helper.accessor((row) => row.metadata.updated_at, {
    id: "updated_at",
    header: (info) => <Header info={info}>Updated at</Header>,
    cell: (info) => (
      <Cell info={info}>
        {info.getValue() ? <DisplayDateTime value={info.getValue()} /> : "-"}
      </Cell>
    ),
    size: 300,
  }),
  helper.display({
    id: "actions",
    cell: ({ row, table }) => {
      if (!table.options.meta?.actions) return null;

      return (
        <AgentVersionActionsMenu
          version={row.original}
          onDelete={table.options.meta?.actions.onDelete}
          onDuplicate={table.options.meta?.actions.onDuplicate}
          onPublish={table.options.meta?.actions.onPublish}
        />
      );
    },
    size: 100,
  }),
];

const AgentVersionActionsMenu: React.FC<{
  version: AgentVersion;
  onDuplicate: (version: AgentVersion) => void;
  onPublish: (version: AgentVersion) => void;
  onDelete?: (version: AgentVersion) => void;
}> = ({ version, onDuplicate, onPublish, onDelete }) => {
  const options = [
    {
      key: "Duplicate",
      icon: faCopy,
      action: () => onDuplicate(version),
    },
    version.status === "draft" && {
      key: "Publish",
      icon: faCheckCircle,
      action: () => onPublish(version),
    },
    !!onDelete && {
      key: "Delete",
      icon: faTrash,
      action: () => onDelete(version),
      destructive: true,
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  return (
    <div className="flex items-end justify-end pr-5">
      <EllipsisOptionsDropdown
        buttonClassName="text-gray-300 w-5 opacity-0 group-hover:opacity-100 data-[menu-open=true]:opacity-100"
        dataLoc="agent-version-actions"
        elements={options}
        ellipsisDirection="vertical"
        timeoutDuration={200}
      />
    </div>
  );
};
